import type { CharacterWithUrls } from 'schemas';
import { ErrorCodes } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { handleApiError } from '@/api/helpers/handleApiError';
import { supabase } from '@/supabase/client';
import {
  CHARACTER_FILE_PATTERNS,
  STORAGE_BUCKETS,
  SIGNED_URL_EXPIRATION,
} from 'schemas';

export const fetchCharacters = async (): Promise<CharacterWithUrls[]> => {
  try {
    // Fetch characters from the database (excluding soft deleted ones)
    const { data: characters, error } = await supabase
      .from('characters')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      throw new ApiError({
        type: 'error',
        error: 'Failed to fetch characters',
        code: ErrorCodes.INTERNAL_SERVER_ERROR,
        message: error.message || 'Failed to fetch characters',
      });
    }

    if (!characters) {
      return [];
    }

    // For each character, get their reference image and character preview image URLs
    const charactersWithImages = await Promise.all(
      characters.map(async (character) => {
        const characterWithImages: CharacterWithUrls = {
          ...character,
        };

        // Get reference image URL (first one only)
        try {
          const { data: referenceImageFiles, error: listError } =
            await supabase.storage
              .from(STORAGE_BUCKETS.CHARACTER_IMAGES)
              .list(
                CHARACTER_FILE_PATTERNS.REFERENCE_IMAGES_DIR(
                  character.user_id,
                  character.id
                )
              );

          if (
            !listError &&
            referenceImageFiles &&
            referenceImageFiles.length > 0
          ) {
            const { data } = await supabase.storage
              .from(STORAGE_BUCKETS.CHARACTER_IMAGES)
              .createSignedUrl(
                CHARACTER_FILE_PATTERNS.REFERENCE_IMAGE_PATH(
                  character.user_id,
                  character.id,
                  referenceImageFiles[0].name.split('.')[0], // Remove extension to get imageId
                  referenceImageFiles[0].name.split('.').pop() || 'jpg' // Get extension
                ),
                SIGNED_URL_EXPIRATION.STANDARD
              );
            if (data?.signedUrl) {
              characterWithImages.referenceImageUrl = data.signedUrl;
            }
          }
        } catch (error) {
          // Ignore image errors, just log
          console.warn(
            `Could not get reference image for character ${character.id}:`,
            error
          );
        }

        // Get character preview image URL (first one from any style template)
        try {
          // List all subdirectories in character-previews (each subdirectory is a style template ID and holds a preview image in the given style)
          const { data: styleTemplateFolders, error: listFoldersError } =
            await supabase.storage
              .from(STORAGE_BUCKETS.CHARACTER_IMAGES)
              .list(`${character.user_id}/${character.id}/character-previews`);

          if (
            !listFoldersError &&
            styleTemplateFolders &&
            styleTemplateFolders.length > 0
          ) {
            // Get the first style template folder
            const firstStyleTemplateFolder = styleTemplateFolders[0];

            // List images in this style template folder
            const { data: previewImageFiles, error: listImagesError } =
              await supabase.storage
                .from(STORAGE_BUCKETS.CHARACTER_IMAGES)
                .list(
                  `${character.user_id}/${character.id}/character-previews/${firstStyleTemplateFolder.name}`
                );

            if (
              !listImagesError &&
              previewImageFiles &&
              previewImageFiles.length > 0
            ) {
              const fileName = previewImageFiles[0].name;
              const imageId = fileName.split('.')[0];
              const extension = fileName.split('.').pop() || 'png';
              const { data } = await supabase.storage
                .from(STORAGE_BUCKETS.CHARACTER_IMAGES)
                .createSignedUrl(
                  CHARACTER_FILE_PATTERNS.CHARACTER_PREVIEW_IMAGE_PATH(
                    character.user_id,
                    character.id,
                    firstStyleTemplateFolder.name, // styleTemplateId
                    imageId,
                    extension
                  ),
                  SIGNED_URL_EXPIRATION.STANDARD
                );
              if (data?.signedUrl) {
                characterWithImages.previewImageUrl = data.signedUrl;
              }
            }
          }
        } catch (error) {
          // Ignore image errors, just log
          console.warn(
            `Could not get character preview image for character ${character.id}:`,
            error
          );
        }

        return characterWithImages;
      })
    );

    return charactersWithImages;
  } catch (error) {
    handleApiError(error);
  }
};
